import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Modal, NeonBadge, Spinner } from './UI';
import { inputStyle, btnPrimary, colors, fonts } from '../lib/styles';

export default function ListingDetail({ listing, currentUser, onClose, onBidPlaced }) {
  const [bidAmount, setBidAmount] = useState('');
  const [bidNote, setBidNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Message state
  const [msgText, setMsgText] = useState('');
  const [msgSent, setMsgSent] = useState(false);
  const [msgLoading, setMsgLoading] = useState(false);

  const isOwner = currentUser && listing.user_id === currentUser.id;
  const alreadyBid = listing.bids?.some(b => b.user_id === currentUser?.id);

  const handleBid = async () => {
    if (!currentUser) return setError('You must be logged in to bid.');
    if (!bidAmount) return setError('Enter a bid amount.');
    setLoading(true); setError('');
    try {
      const { error: bidError } = await supabase.from('bids').insert({
        listing_id: listing.id,
        user_id: currentUser.id,
        amount: parseInt(bidAmount),
        note: bidNote,
      });
      if (bidError) throw bidError;
      setSuccess(true);
      onBidPlaced();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleMessage = async () => {
    if (!currentUser) return;
    if (!msgText.trim()) return;
    setMsgLoading(true);
    try {
      await supabase.from('messages').insert({
        listing_id: listing.id,
        sender_id: currentUser.id,
        recipient_id: listing.user_id,
        content: msgText.trim(),
      });
      setMsgSent(true);
    } catch (err) {
      console.error(err);
    }
    setMsgLoading(false);
  };

  const timeAgo = (ts) => {
    const diff = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <Modal onClose={onClose}>
      <div style={{ padding: '32px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <NeonBadge color={listing.urgent ? colors.red : colors.cyan}>{listing.category}</NeonBadge>
            {listing.urgent && <NeonBadge color={colors.red}>URGENT</NeonBadge>}
            {listing.pricing_type === 'bid' && <NeonBadge color={colors.purple}>OPEN TO BIDS</NeonBadge>}
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: `1px solid ${colors.borderMid}`, color: colors.textDim,
            borderRadius: '2px', padding: '4px 10px', cursor: 'pointer',
            fontFamily: fonts.mono, fontSize: '11px',
          }}>✕ CLOSE</button>
        </div>

        <h2 style={{ fontFamily: fonts.display, fontSize: '20px', fontWeight: 800, color: colors.text, margin: '0 0 10px', letterSpacing: '0.04em' }}>
          {listing.title}
        </h2>

        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <span style={{ fontFamily: fonts.mono, fontSize: '11px', color: colors.textDim }}>📍 {listing.location}</span>
          <span style={{ fontFamily: fonts.mono, fontSize: '11px', color: colors.textDim }}>
            by <span style={{ color: '#00ffc870' }}>{listing.profiles?.handle || 'anonymous'}</span>
          </span>
          <span style={{ fontFamily: fonts.mono, fontSize: '11px', color: colors.textFaint }}>{timeAgo(listing.created_at)}</span>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.02)', border: `1px solid ${colors.border}`,
          borderRadius: '4px', padding: '16px', marginBottom: '24px',
        }}>
          <p style={{ fontFamily: fonts.mono, fontSize: '13px', color: '#ffffffa0', margin: 0, lineHeight: 1.7 }}>
            {listing.description}
          </p>
        </div>

        {listing.pricing_type === 'fixed' && (
          <div style={{
            background: 'rgba(0,255,200,0.05)', border: '1px solid #00ffc820',
            borderRadius: '4px', padding: '14px 18px', marginBottom: '24px',
            display: 'flex', alignItems: 'center', gap: '14px',
          }}>
            <span style={{ fontFamily: fonts.mono, fontSize: '11px', color: '#00ffc860', letterSpacing: '0.1em' }}>FIXED RATE</span>
            <span style={{ fontFamily: fonts.display, fontSize: '26px', fontWeight: 800, color: colors.cyan }}>${listing.price}</span>
          </div>
        )}

        {/* Bids */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{
            fontFamily: fonts.display, fontSize: '11px', fontWeight: 700,
            color: colors.purple, letterSpacing: '0.12em', textTransform: 'uppercase',
            margin: '0 0 12px', borderBottom: `1px solid #a78bfa20`, paddingBottom: '8px',
          }}>
            {listing.bids?.length || 0} BID{listing.bids?.length !== 1 ? 'S' : ''} SUBMITTED
          </h3>

          {!listing.bids?.length ? (
            <p style={{ fontFamily: fonts.mono, fontSize: '12px', color: colors.textFaint }}>No bids yet. Be the first.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {listing.bids.map((bid, i) => (
                <div key={i} style={{
                  background: 'rgba(167,139,250,0.05)', border: '1px solid #a78bfa20',
                  borderRadius: '4px', padding: '12px 16px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <span style={{ fontFamily: fonts.display, fontSize: '11px', color: colors.purple, fontWeight: 700 }}>
                      {bid.profiles?.handle || 'anonymous'}
                    </span>
                    {bid.note && (
                      <p style={{ fontFamily: fonts.mono, fontSize: '11px', color: colors.textDim, margin: '4px 0 0' }}>{bid.note}</p>
                    )}
                  </div>
                  <span style={{ fontFamily: fonts.display, fontSize: '20px', fontWeight: 800, color: colors.purple }}>${bid.amount}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Place Bid Section */}
        {!isOwner && currentUser && (
          <>
            {success || alreadyBid ? (
              <div style={{
                background: 'rgba(0,255,200,0.08)', border: '1px solid #00ffc840',
                borderRadius: '4px', padding: '20px', textAlign: 'center',
                boxShadow: '0 0 30px #00ffc815', marginBottom: '20px',
              }}>
                <div style={{ fontFamily: fonts.display, fontSize: '16px', color: colors.cyan, marginBottom: '6px' }}>◈ BID TRANSMITTED</div>
                <p style={{ fontFamily: fonts.mono, fontSize: '12px', color: colors.textDim, margin: 0 }}>
                  Your bid is in the network. The poster will reach out.
                </p>
              </div>
            ) : (
              <div style={{
                background: 'rgba(0,255,200,0.03)', border: '1px solid #00ffc815',
                borderRadius: '4px', padding: '20px', marginBottom: '20px',
              }}>
                <h3 style={{ fontFamily: fonts.display, fontSize: '11px', fontWeight: 700, color: colors.cyan, letterSpacing: '0.12em', margin: '0 0 14px' }}>
                  {listing.pricing_type === 'bid' ? 'PLACE YOUR BID' : 'SUBMIT APPLICATION'}
                </h3>
                {error && <p style={{ fontFamily: fonts.mono, fontSize: '12px', color: colors.red, marginBottom: '12px' }}>{error}</p>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontFamily: fonts.display, color: colors.cyan, fontSize: '13px' }}>$</span>
                    <input value={bidAmount} onChange={e => setBidAmount(e.target.value.replace(/\D/g, ''))}
                      placeholder={listing.pricing_type === 'fixed' ? listing.price : 'Your price'}
                      style={{ ...inputStyle, paddingLeft: '28px' }} />
                  </div>
                  <textarea value={bidNote} onChange={e => setBidNote(e.target.value)}
                    placeholder="Why you're the right operator for this... (optional)"
                    rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                  <button onClick={handleBid} disabled={loading || !bidAmount} style={{ ...btnPrimary(loading || !bidAmount), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    {loading && <Spinner size={14} color="#000" />}
                    TRANSMIT BID
                  </button>
                </div>
              </div>
            )}

            {/* Message poster */}
            <div style={{
              background: 'rgba(255,255,255,0.02)', border: `1px solid ${colors.border}`,
              borderRadius: '4px', padding: '20px',
            }}>
              <h3 style={{ fontFamily: fonts.display, fontSize: '11px', fontWeight: 700, color: colors.textMid, letterSpacing: '0.12em', margin: '0 0 14px' }}>
                MESSAGE POSTER
              </h3>
              {msgSent ? (
                <p style={{ fontFamily: fonts.mono, fontSize: '12px', color: colors.cyan }}>✓ Message sent.</p>
              ) : (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input value={msgText} onChange={e => setMsgText(e.target.value)}
                    placeholder="Send a quick message..."
                    style={{ ...inputStyle, flex: 1 }}
                    onKeyDown={e => e.key === 'Enter' && handleMessage()} />
                  <button onClick={handleMessage} disabled={msgLoading || !msgText.trim()} style={{
                    ...btnPrimary(msgLoading || !msgText.trim()),
                    padding: '10px 16px', whiteSpace: 'nowrap', fontSize: '11px',
                  }}>
                    {msgLoading ? <Spinner size={14} color="#000" /> : 'SEND'}
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {!currentUser && (
          <div style={{
            textAlign: 'center', padding: '20px',
            border: `1px solid ${colors.border}`, borderRadius: '4px',
            fontFamily: fonts.mono, fontSize: '13px', color: colors.textDim,
          }}>
            <span style={{ color: colors.cyan, cursor: 'pointer' }}>Log in</span> to place a bid or message the poster.
          </div>
        )}
      </div>
    </Modal>
  );
}
